import { Expense } from '@otw/models'
import { useQuery } from '@tanstack/react-query'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { db } from 'config/firebase'

export interface RepairSubcategoryImpact {
  /** Distinct expenses where the label appears (in repairSubcategories or any lineItem). */
  expenseCount: number
  /** Up to 5 sample expenses for preview UI. */
  sampleExpenses: Array<Pick<Expense, 'id' | 'description' | 'date' | 'amount' | 'vehicleId'>>
  /** Number of individual line items that reference the label. */
  lineItemMatches: number
}

/**
 * Returns how many expenses (and line items) currently reference a given
 * repair subcategory label. Used by the manage-taxonomy dialog to preview the
 * impact of a rename or delete before the user confirms.
 *
 * Skips the query when `label` is empty so it can be used with a controlled
 * dialog state without firing a request for nothing.
 */
export const useRepairSubcategoryImpact = (label: string | null | undefined) => {
  return useQuery<RepairSubcategoryImpact>({
    queryKey: ['repairSubcategoryImpact', label],
    enabled: !!label,
    queryFn: async () => {
      if (!label) {
        return { expenseCount: 0, sampleExpenses: [], lineItemMatches: 0 }
      }
      const expensesCollection = collection(db, 'expenses')
      const snapshot = await getDocs(
        query(expensesCollection, where('repairSubcategories', 'array-contains', label)),
      )

      let lineItemMatches = 0
      const sampleExpenses: RepairSubcategoryImpact['sampleExpenses'] = []
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data() as Partial<Expense>
        if (Array.isArray(data.lineItems)) {
          lineItemMatches += data.lineItems.filter(li => li?.subcategory === label).length
        }
        if (sampleExpenses.length < 5) {
          sampleExpenses.push({
            id: docSnap.id,
            description: data.description ?? '',
            date:
              data.date instanceof Date
                ? data.date
                : data.date
                  ? new Date(data.date as unknown as string)
                  : new Date(0),
            amount: typeof data.amount === 'number' ? data.amount : 0,
            vehicleId: data.vehicleId ?? null,
          })
        }
      }

      return {
        expenseCount: snapshot.size,
        sampleExpenses,
        lineItemMatches,
      }
    },
    staleTime: 1000 * 30,
  })
}
